import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '../../internal';

export const NODE_ENTITY_TOKEN = new StateToken<NodeEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.NODE_ENTITY);

export type NodeEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.NodeEntityModels.NodeEntityType>;

@State<NodeEntityStateModel>({
  name: NODE_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class NodeEntityState extends HistoryEntityBaseState<FlatModels.NodeEntityModels.NodeEntityType, NodeEntityStateModel>{
  //<editor-fold desc="Selectors">

  @Selector([NodeEntityState])
  static override typedEntities$(state: NodeEntityStateModel): { [id: string]: FlatModels.NodeEntityModels.NodeEntityType } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([NodeEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }): FlatModels.NodeEntityModels.NodeEntityType[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([NodeEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([NodeEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  @Selector([FlatStates.NavigationControlState.slugs$])
  static isTraining$(slugs: FlatModels.NavigationControlModels.PathModel | null) {
    return slugs && slugs.type === FlatModels.NavigationControlModels.PathModelType.enum.TRAINING;
  }

  @Selector([FlatStates.NavigationControlState.blockSlug$])
  static isTrainingResult$(subjectSlug: string | null) {
    return subjectSlug && subjectSlug === 'results' || false;
  }

  @Selector([NodeEntityState.typedEntities$])
  static typedRouteMaps$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    const typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement } = {};
    const typedRouteMap: { [path: string]: string } = {};
    const typedParentRouteMap: { [id: string]: string[] } = {};

    let lastRetryNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType } = {};
    let retryNodes: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType };
    do {
      retryNodes = {};
      for (const [id, node] of Object.entries(typedEntities)) {
        let path = node.route as string;
        if (node.parentId === null) {
          // Typed Route Element
          typedRouteElements[id] = {
            id,
            route: node.route as string,
            path,
            depth: 0,
            hasChildren: node.children?.length || 0,
            sort: node.position?.toString() || '0',
            parentId: null,
            settings: node.settings || {},
          };

          // Typed Route Map
          typedRouteMap[path] = id;

          // Typed Parent Route Map
          if ((typedParentRouteMap['null'] || []).indexOf(id) === -1) {
            typedParentRouteMap['null'] = [...(typedParentRouteMap['null'] || []), id];
          }
        } else {
          const parentRouteElement = typedRouteElements[node.parentId];
          if (parentRouteElement) {
            // Path
            path = parentRouteElement.path + '/' + path;

            // Typed Route Element
            typedRouteElements[id] = {
              id,
              route: node.route as string,
              path,
              depth: parentRouteElement.depth + 1,
              hasChildren: node.children?.length || 0,
              sort: parentRouteElement.sort + '.' + node.position?.toString() || '0',
              parentId: node.parentId,
              settings: Object.assign({}, typedRouteElements[node.parentId]?.settings, node.settings || {}),
            };

            // Typed Route Map
            typedRouteMap[path] = id;

            // Typed Parent Route Map
            if ((typedParentRouteMap[parentRouteElement.id] || []).indexOf(id) === -1) {
              typedParentRouteMap[parentRouteElement.id] = [...(typedParentRouteMap[parentRouteElement.id] || []), id];
            }
          } else {
            retryNodes[id] = node;
          }
        }
      }
      if (Object.values(retryNodes).length === Object.values(lastRetryNodes).length) {
        break;
      }
      lastRetryNodes = retryNodes;
    } while (Object.values(retryNodes).length > 0)

    return { typedRouteElements, typedRouteMap, typedParentRouteMap };
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.typedRouteMaps$
  ])
  static typedElearnings$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                          typedRouteMaps: { typedParentRouteMap: { [id: string]: string[] }}) {
    return (typedRouteMaps.typedParentRouteMap['null'] || []).reduce((arr, id) => {
      const entity = typedEntities[id];
      if (entity) {
        arr[entity.id] = entity;
      }
      return arr;
    }, {} as { [id: string]: FlatModels.NodeEntityModels.NodeEntityType });
  }

  @Selector([NodeEntityState.typedElearnings$])
  static elearnings$(typedElearnings: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) {
    return Object.values(typedElearnings).sort((a, b) => a.position - b.position);
  }

  //<editor-fold desc="Route Elements">

  @Selector([NodeEntityState.typedRouteMaps$])
  static typedRouteElements$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }}) {
    return typedRouteMaps.typedRouteElements;
  }

  @Selector([NodeEntityState.typedRouteElements$])
  static routeElements$(typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }) {
    return Object.values(typedRouteElements);
  }

  @Selector([NodeEntityState.typedRouteElements$])
  static routeElement$(typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }) {
    return (id: string) => {
      return typedRouteElements && typedRouteElements[id] || null;
    }
  }

  @Selector([
    NodeEntityState.typedRouteMaps$,
    FlatStates.NavigationControlState.elearningUrl$
  ])
  static currentElearningRouteElement$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }, typedRouteMap: { [path: string]: string }},
                                       slideElearningUrl: string | null) {
    if (slideElearningUrl) {
      const id = typedRouteMaps.typedRouteMap[slideElearningUrl];
      if (id) {
        const entity = typedRouteMaps.typedRouteElements[id];
        if (entity.depth === 0) {
          return entity;
        }
      }
    }
    return null;
  }

  @Selector([
    NodeEntityState.typedRouteMaps$,
    FlatStates.NavigationControlState.subjectUrl$
  ])
  static currentSubjectRouteElement$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }, typedRouteMap: { [path: string]: string }},
                                     slideSubjectUrl: string | null) {
    if (slideSubjectUrl) {
      const id = typedRouteMaps.typedRouteMap[slideSubjectUrl];
      if (id) {
        const entity = typedRouteMaps.typedRouteElements[id];
        if (entity.depth === 1) {
          return entity;
        }
      }
    }
    return null;
  }

  @Selector([
    NodeEntityState.typedRouteMaps$,
    FlatStates.NavigationControlState.chapterUrl$
  ])
  static currentChapterRouteElement$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }, typedRouteMap: { [path: string]: string }},
                                     slideChapterUrl: string | null) {
    if (slideChapterUrl) {
      const id = typedRouteMaps.typedRouteMap[slideChapterUrl];
      if (id) {
        const entity = typedRouteMaps.typedRouteElements[id];
        if (entity.depth === 2) {
          return entity;
        }
      }
    }
    return null;
  }

  @Selector([
    NodeEntityState.typedRouteMaps$,
    FlatStates.NavigationControlState.blockUrl$
  ])
  static currentBlockRouteElement$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }, typedRouteMap: { [path: string]: string }},
                                   slideBlockUrl: string | null) {
    if (slideBlockUrl) {
      const id = typedRouteMaps.typedRouteMap[slideBlockUrl];
      if (id) {
        const entity = typedRouteMaps.typedRouteElements[id];
        if (entity.depth === 3) {
          return entity;
        }
      }
    }
    return null;
  }

  @Selector([
    NodeEntityState.typedRouteMaps$,
    FlatStates.NavigationControlState.slideUrl$
  ])
  static currentSlideRouteElement$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }, typedRouteMap: { [path: string]: string }},
                                   slideSlideUrl: string | null) {
    if (slideSlideUrl) {
      const id = typedRouteMaps.typedRouteMap[slideSlideUrl];
      if (id) {
        const entity = typedRouteMaps.typedRouteElements[id];
        if (entity.depth === 4) {
          return entity;
        }
      }
    }
    return null;
  }

  @Selector([
    NodeEntityState.typedRouteMaps$,
    FlatStates.NavigationControlState.currentUrl$
  ])
  static currentSelectedRouteElement$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }, typedRouteMap: { [path: string]: string }},
                                      currentUrl: string) {
    if (currentUrl) {
      const id = typedRouteMaps.typedRouteMap[currentUrl];
      if (id) {
        return typedRouteMaps.typedRouteElements[id];
      }
    }
    return null;
  }

  @Selector([
    NodeEntityState.typedRouteMaps$,
    NodeEntityState.currentSelectedRouteElement$
  ])
  static currentSelectedParentRouteElement$(typedRouteMaps: { typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }, typedRouteMap: { [path: string]: string }},
                                            currentSelectedRouteElement: FlatModels.NodeEntityModels.RouteElement) {
    if (currentSelectedRouteElement && currentSelectedRouteElement.parentId) {
      return typedRouteMaps.typedRouteElements[currentSelectedRouteElement.parentId];
    }
    return null;
  }

  //</editor-fold>

  //<editor-fold desc="Current Selected">

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentElearningRouteElement$
  ])
  static currentSelectedElearningNode$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                       currentElearningRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    return currentElearningRouteElement?.id && typedEntities[currentElearningRouteElement.id] || null;
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentSubjectRouteElement$
  ])
  static currentSelectedSubjectNode$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                     currentSubjectRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    return currentSubjectRouteElement?.id && typedEntities[currentSubjectRouteElement.id] || null;
  }

  @Selector([NodeEntityState.currentSelectedRouteElement$])
  static isSubject$(currentSelectedRouteElement: FlatModels.NodeEntityModels.RouteElement) {
    return currentSelectedRouteElement && currentSelectedRouteElement.depth === 1;
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.typedRouteMaps$,
    NodeEntityState.currentSubjectRouteElement$
  ])
  static currentSelectedChapterNodes$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                      typedRouteMaps: { typedParentRouteMap: { [id: string]: string[] }},
                                      currentSubjectRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    if (currentSubjectRouteElement) {
      return (typedRouteMaps.typedParentRouteMap[currentSubjectRouteElement.id] || []).reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.NodeEntityModels.NodeEntityType[]);
    }
    return [];
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentChapterRouteElement$
  ])
  static currentSelectedChapterNode$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                     currentChapterRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    return currentChapterRouteElement?.id && typedEntities[currentChapterRouteElement.id] || null;
  }

  @Selector([NodeEntityState.currentSelectedRouteElement$])
  static isChapter$(currentSelectedRouteElement: FlatModels.NodeEntityModels.RouteElement) {
    return currentSelectedRouteElement && currentSelectedRouteElement.depth === 2;
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.typedRouteMaps$,
    NodeEntityState.currentChapterRouteElement$
  ])
  static currentSelectedBlockNodes$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                    typedRouteMaps: { typedParentRouteMap: { [id: string]: string[] }},
                                    currentChapterRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    if (currentChapterRouteElement) {
      return (typedRouteMaps.typedParentRouteMap[currentChapterRouteElement.id] || []).reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.NodeEntityModels.NodeEntityType[]);
    }
    return [];
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentBlockRouteElement$
  ])
  static currentSelectedBlockNode$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                   currentBlockRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    return currentBlockRouteElement?.id && typedEntities[currentBlockRouteElement.id] || null;
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.typedRouteMaps$,
    NodeEntityState.currentBlockRouteElement$
  ])
  static currentSelectedSlideNodes$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                    typedRouteMaps: { typedParentRouteMap: { [id: string]: string[] }},
                                    currentBlockRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    if (currentBlockRouteElement) {
      return (typedRouteMaps.typedParentRouteMap[currentBlockRouteElement.id] || []).reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.NodeEntityModels.NodeEntityType[]);
    }
    return [];
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentSlideRouteElement$,
    FlatStates.NavigationControlState.nodeIdSlug$
  ])
  static currentSelectedSlideNode$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                   currentSlideRouteElement: FlatModels.NodeEntityModels.RouteElement | null,
                                   slideIdSlug: string | null) {
    if (currentSlideRouteElement) {
      return typedEntities[currentSlideRouteElement.id] || null;
    }
    return slideIdSlug && typedEntities[slideIdSlug] || null;
  }
  
  @Selector([NodeEntityState.currentSelectedRouteElement$])
  static isSlide$(currentSelectedRouteElement: FlatModels.NodeEntityModels.RouteElement) {
    return currentSelectedRouteElement && currentSelectedRouteElement.depth === 4 || false;
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentSelectedRouteElement$
  ])
  static currentSelectedNode$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                              currentSelectedRouteElement: FlatModels.NodeEntityModels.RouteElement | null) {
    return currentSelectedRouteElement?.id && typedEntities[currentSelectedRouteElement.id] || null;
  }
  
  @Selector([
    NodeEntityState.currentSelectedElearningNode$,
    NodeEntityState.currentSelectedSubjectNode$,
    NodeEntityState.currentSelectedChapterNode$,
    NodeEntityState.currentSelectedBlockNode$,
    NodeEntityState.currentSelectedSlideNode$
  ])
  static isNodeCurrent$(currentSelectedElearningNode: FlatModels.NodeEntityModels.NodeEntityType,
                        currentSelectedSubjectNode: FlatModels.NodeEntityModels.NodeEntityType,
                        currentSelectedChapterNode: FlatModels.NodeEntityModels.NodeEntityType,
                        currentSelectedBlockNode: FlatModels.NodeEntityModels.NodeEntityType,
                        currentSelectedSlideNode: FlatModels.NodeEntityModels.NodeEntityType,) {
    return (id: string) => {
      return [currentSelectedElearningNode?.id, currentSelectedSubjectNode?.id, currentSelectedChapterNode?.id, currentSelectedBlockNode?.id, currentSelectedSlideNode?.id].includes(id) || false;
    }
  }
  
  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.typedElearnings$,
    NodeEntityState.currentSelectedRouteElement$
  ])
  static currentSelectedSiblingNodes$(typedEntities: ReturnType<typeof NodeEntityState.typedEntities$>,
                                      typedElearnings: ReturnType<typeof NodeEntityState.typedEntities$>,
                                      currentSelectedRouteElement: ReturnType<typeof NodeEntityState.currentSelectedRouteElement$>) {
    if (!currentSelectedRouteElement) {
      return null;
    }
    const node = typedEntities[currentSelectedRouteElement.id];
    if (node.parentId) {
      const parentNode = typedEntities[node.parentId];
      return parentNode.children
        .filter(childId => childId !== node.id)
        .map(childId => typedEntities[childId])
    } else {
      return Object.values(typedElearnings)
        .filter(entity => entity.id !== node.id);
    }
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentSelectedRouteElement$
  ])
  static currentSelectedNodeWithParents$(typedEntities: ReturnType<typeof NodeEntityState.typedEntities$>,
                                         currentSelectedRouteElement: ReturnType<typeof NodeEntityState.currentSelectedRouteElement$>) {
    if (!currentSelectedRouteElement) {
      return null;
    }

    const node = typedEntities[currentSelectedRouteElement.id];
    let parentId = node.parentId
    
    const parentNodes = [];
    while (parentId) {
      const parentNode = typedEntities[parentId];
      parentId = parentNode?.parentId;
      if(parentNode) parentNodes.push(parentNode);
    }

    return structuredClone({
      node,
      parentNodes
    })
  }

  @Selector([
    NodeEntityState.currentSelectedNodeWithParents$
  ])
  static currentSelectedNodeSettings$(currentSelectedNodeWithParents: ReturnType<typeof NodeEntityState.currentSelectedNodeWithParents$>) {
    const { node, parentNodes } = currentSelectedNodeWithParents ?? {};
    if (!node || !parentNodes) {
      return null;
    }
    const settings = FlatModels.NodeEntityModels.determineSettings(node, parentNodes);
    return {
      node,
      settings,
    };
  }
  
  @Selector([
    NodeEntityState.currentSelectedNodeWithParents$
  ])
  static selectedNodeAndParentsSettings$(currentSelectedNodeWithParents: ReturnType<typeof NodeEntityState.currentSelectedNodeWithParents$>) {
    const { node, parentNodes } = currentSelectedNodeWithParents ?? {};
    if (!node || !parentNodes) {
      return null;
    }
    const currentSettings = FlatModels.NodeEntityModels.determineSettings(node, parentNodes);

    const parentNodesCopy = [...parentNodes];
    const parentsSettings = parentNodes.map((node) => {
      parentNodesCopy.shift();
      const settings = FlatModels.NodeEntityModels.determineSettings(node, parentNodesCopy);
      return {
        settings,
        node
      }
    })

    return {
      node,
      currentSettings,
      parentsSettings,
    };
  }

  @Selector([NodeEntityState.currentSelectedNodeSettings$])
  static currentSelectedSettings$(currentSelectedNodeSettings: ReturnType<typeof NodeEntityState.currentSelectedNodeSettings$>) {
    return currentSelectedNodeSettings?.settings.combinedWithDefaults;
  }

  @Selector([
    NodeEntityState.currentSelectedElearningNode$,
    NodeEntityState.currentSelectedSubjectNode$,
    NodeEntityState.currentSelectedChapterNode$,
    NodeEntityState.currentSelectedBlockNode$,
    NodeEntityState.currentSelectedSlideNode$,
  ])
  static currentSelectedNodes$(elearningNode: FlatModels.NodeEntityModels.NodeEntityType,
                               subjectNode: FlatModels.NodeEntityModels.NodeEntityType,
                               chapterNode: FlatModels.NodeEntityModels.NodeEntityType,
                               blockNode: FlatModels.NodeEntityModels.NodeEntityType,
                               slideNode: FlatModels.NodeEntityModels.NodeEntityType) {
    const nodes: FlatModels.NodeEntityModels.NodeEntityType[] = [];
    for (const node of [elearningNode, subjectNode, chapterNode, blockNode, slideNode]) {
      if (!node) {
        break;
      }
      nodes.push(node);
    }
    return nodes;
  }

  @Selector([
    NodeEntityState.typedEntities$,
    NodeEntityState.currentSelectedBlockNode$
  ])
  static currentSelectedCaseStartNode$(typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
                                       block: FlatModels.NodeEntityModels.NodeEntityType | null) {
    if (block && block.children.length > 0) {
      return typedEntities[block.children[0]];
    }
    return null;
  }

  @Selector([
    NodeEntityState.currentSelectedCaseStartNode$,
    FlatStates.NavigationControlState.blockUrl$
  ])
  static currentSelectedCaseStartUrl$(caseStartNode: FlatModels.NodeEntityModels.NodeEntityType | null,
                                      blockUrl: string | null) {
    return caseStartNode && blockUrl && blockUrl + '/' + caseStartNode.route || null;
  }

  @Selector([
    NodeEntityState.currentSelectedElearningNode$,
    NodeEntityState.currentSelectedSubjectNode$,
    NodeEntityState.currentSelectedChapterNode$,
    NodeEntityState.currentSelectedBlockNode$,
    NodeEntityState.currentSelectedSlideNode$
  ])
  static currentTypedExpanded$(elearning: FlatModels.NodeEntityModels.NodeEntityType,
                               subject: FlatModels.NodeEntityModels.NodeEntityType,
                               chapter: FlatModels.NodeEntityModels.NodeEntityType,
                               block: FlatModels.NodeEntityModels.NodeEntityType,
                               slide: FlatModels.NodeEntityModels.NodeEntityType) {
    const typedExpanded: { [id: string]: boolean } = {};

    if (elearning) {
      typedExpanded[elearning.id] = true;
    }

    if (subject) {
      typedExpanded[subject.id] = true;
    }

    if (chapter) {
      typedExpanded[chapter.id] = true;
    }

    if (block) {
      typedExpanded[block.id] = true;
    }
    
    if (slide) {
      typedExpanded[slide.id] = true;
    }

    return typedExpanded;
  }

  //</editor-fold>

  static nodeById$(id: string | null) {
    return createSelector([NodeEntityState.typedEntities$], (typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType }) => {
      return id && typedEntities[id] || null;
    });
  }

  static nodesByParentId$(parentId: string | null) {
    return createSelector(
      [
        NodeEntityState.typedEntities$,
        NodeEntityState.typedRouteMaps$
      ],
      (
        typedEntities: { [id: string]: FlatModels.NodeEntityModels.NodeEntityType },
        typedRouteMaps: { typedParentRouteMap: { [id: string]: string[] }}
      ) => {
        if (parentId === null) {
          parentId = 'null';
        }
        return (typedRouteMaps.typedParentRouteMap[parentId] || []).reduce((arr, id) => {
          const entity = typedEntities[id];
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, [] as FlatModels.NodeEntityModels.NodeEntityType[]);
      }
    );
  }

  @Selector([FlatStates.AuthControlState.currentUser$, NodeEntityState.typedEntities$])
  static canWriteNode$(
    currentUser: ReturnType<typeof FlatStates.AuthControlState.currentUser$>,
    typedEntities: ReturnType<typeof NodeEntityState.typedEntities$>
  ) {
    return (nodeId?: string) => {
      // Prevent "/slide/[nodeId]" - route permission to fail, when we don't have an assigned node
      if (!nodeId) {
        return true;
      }
      let node: FlatModels.NodeEntityModels.Node | null = typedEntities[nodeId];
      if (!node) return false;

      while (node) {
        if (FlatModels.UserEntityModels.canWriteNode(currentUser, node)) return true;
        node = node.parentId ? typedEntities[node.parentId] : null;
      }
      return false;
    };
  }

  @Selector([FlatStates.AuthControlState.currentUser$, NodeEntityState.typedEntities$])
  static canReadNode$(
    currentUser: ReturnType<typeof FlatStates.AuthControlState.currentUser$>,
    typedEntities: ReturnType<typeof NodeEntityState.typedEntities$>
  ) {
    return (nodeId?: string) => {
      let tmpNode = nodeId ? typedEntities[nodeId] : null;
      if (!tmpNode) return false;
      const nodes = [];
      while (tmpNode) {
        nodes.push(tmpNode);
        tmpNode = tmpNode.parentId ? typedEntities[tmpNode.parentId] : null;
      }
      const aclResult = FlatModels.UserEntityModels.applyReadACL(nodes, currentUser, false);
      return aclResult.ok;
    };
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.NodeEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<NodeEntityStateModel>,
                            action: FlatActions.NodeEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.NodeEntityActions.GetRootEntitySuccess)
  getRootEntitySuccess(state: StateContext<NodeEntityStateModel>,
                       action: FlatActions.NodeEntityActions.GetRootEntitySuccess) {
    return this.getEntitySuccess(state, action);
  }

  @Action(FlatActions.NodeEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<NodeEntityStateModel>,
                              action: FlatActions.NodeEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.NodeEntityActions.GetRootEntitiesSuccess)
  getRootEntitiesSuccess(state: StateContext<NodeEntityStateModel>,
                         action: FlatActions.NodeEntityActions.GetEntitiesSuccess) {
    return this.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.NodeEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<NodeEntityStateModel>,
                               action: FlatActions.NodeEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.NodeEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<NodeEntityStateModel>,
                                 action: FlatActions.NodeEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.NodeEntityActions.AddEntity)
  @Undoable(
    FlatActions.NodeEntityActions.AddEntity,
    NODE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<NodeEntityStateModel>,
                     action: FlatActions.NodeEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.NodeEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.NodeEntityActions.UpdateEntity,
    NODE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<NodeEntityStateModel>,
                        action: FlatActions.NodeEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.NodeEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.NodeEntityActions.RemoveEntity,
    NODE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<NodeEntityStateModel>,
                        action: FlatActions.NodeEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.NodeEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<NodeEntityStateModel>,
                        action: FlatActions.NodeEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.NodeEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<NodeEntityStateModel>,
                        action: FlatActions.NodeEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.NodeEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<NodeEntityStateModel>,
                            action: FlatActions.NodeEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  @Action(FlatActions.NodeEntityActions.RearrangeNodes)
  rearrangeNodes({getState, dispatch}: StateContext<NodeEntityStateModel>,
                 {children}: FlatActions.NodeEntityActions.RearrangeNodes) {
    // Get Entities
    const entities = structuredClone(getState().entities);

    // Iterate Through Children
    for (let i = 0; i < children.length; i++) {
      // Get Child
      const child = children[i];

      // Get Entity
      let entity;
      if (typeof child === 'string') {
        entity = entities[child];
      } else {
        entity = entities[child.id];
      }

      // Update Entity
      entity.position = i;

      // Dispatch Update
      dispatch(new FlatActions.NodeEntityActions.UpdateEntity(entity));
    }
  }

  //</editor-fold>
}
